/**
 * Global error handlers
 *
 * Captures uncaught exceptions (`window.error`) and unhandled promise
 * rejections (`window.unhandledrejection`) and routes them through
 * `appLogger.error` so they land in the same analytics pipeline as the
 * rest of the app.
 *
 * Uses `addEventListener` rather than `window.onerror` / `window.onunhandledrejection`
 * so we compose with React/Vite error handlers instead of overwriting them.
 *
 * De-dupes by a short local fingerprint within a 2-second window to keep
 * tight error loops from flooding Firestore. The fingerprint keys on
 * message only — `Window.onError` and `Window.onUnhandledRejection` share
 * one dedupe budget so an identical error surfacing via both paths in the
 * same window collapses to a single log entry.
 */

import { appLogger } from '@/domain/services/analytics/appLogger';
import { computeFingerprint } from '@/domain/services/analytics/fingerprint';

const DEDUPE_WINDOW_MS = 2000;
const DEDUPE_MAX_ENTRIES = 50;

const seen = new Map<string, number>();
let installed = false;

function shouldEmit(message: string): boolean {
  const fp = computeFingerprint({ message });
  const now = Date.now();
  const last = seen.get(fp);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) {
    return false;
  }
  // Re-insert so this becomes the newest entry under Map's insertion-order LRU.
  seen.delete(fp);
  seen.set(fp, now);
  if (seen.size > DEDUPE_MAX_ENTRIES) {
    const oldest = seen.keys().next().value;
    if (oldest !== undefined) seen.delete(oldest);
  }
  return true;
}

function handleErrorEvent(event: ErrorEvent): void {
  const source = 'Window.onError';
  const isRealError = event.error instanceof Error;
  const error = isRealError
    ? (event.error as Error)
    : new Error(event.message || String(event.error ?? 'Unknown error'));
  if (!shouldEmit(error.message)) return;
  // For synthesized errors, the stack lives inside this file (a logger
  // internal) so frame parsing yields nothing. Use the ErrorEvent's own
  // filename/lineno as a fallback so `origin` is still populated.
  const fallbackOrigin = !isRealError
      && typeof event.filename === 'string'
      && event.filename.length > 0
      && Number.isFinite(event.lineno)
    ? `${event.filename}:${event.lineno}`
    : undefined;
  appLogger.error(error.message, source, { error, origin: fallbackOrigin });
}

function handleRejectionEvent(event: PromiseRejectionEvent): void {
  const source = 'Window.onUnhandledRejection';
  const reason = event.reason;
  const error = reason instanceof Error ? reason : new Error(String(reason));
  if (!shouldEmit(error.message)) return;
  appLogger.error(error.message, source, { error });
}

export function installGlobalErrorHandlers(): void {
  if (installed) return;
  if (typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', handleErrorEvent);
  window.addEventListener('unhandledrejection', handleRejectionEvent);
}

export function __resetGlobalErrorHandlersForTesting(): void {
  if (typeof window !== 'undefined') {
    window.removeEventListener('error', handleErrorEvent);
    window.removeEventListener('unhandledrejection', handleRejectionEvent);
  }
  seen.clear();
  installed = false;
}
